import { useEffect, useRef } from 'react';
import { BoltCircle } from '../components/BoltCircle';
import { McList, NumericInput, SliderInput } from '../components/Inputs';
import { Ruler } from '../components/Ruler';
import { Triangle } from '../components/Triangle';
import { disabledButton, ghostButton, primaryButton } from '../components/ui';
import { getTopic } from '../content/topics';
import type { Response } from '../content/types';
import { answerLabel, isCorrect, trapFor } from '../engine/generate';
import { currentQuestion } from '../store/state';
import { useApp, useHaptics } from '../store/useApp';

export function Drill() {
  const { state, dispatch } = useApp();
  const haptic = useHaptics();
  const session = state.session;
  const buzzedFor = useRef<string | null>(null);

  const question = session ? currentQuestion(session) : null;
  const correct = question && session ? isCorrect(question, session.response) : false;

  /*
   * Buzz once per reveal, not once per render. Without the ref this fires
   * again on every unrelated state change while the answer is on screen.
   */
  useEffect(() => {
    if (!session?.revealed) return;
    const key = `${session.index}:${session.draws.length}`;
    if (buzzedFor.current === key) return;
    buzzedFor.current = key;
    haptic(correct ? 'right' : 'wrong');
  }, [session?.revealed, session?.index, session?.draws.length, correct, haptic, session]);

  if (!session || !question) return null;

  const total = session.draws.length;
  const last = session.index + 1 >= total;
  const needsAnswer = session.response === null;
  const trap = session.revealed ? trapFor(question, session.response) : null;

  // The walk-through falls back to the formula and the explanation when a
  // solver produced no steps, so the button is never a dead end.
  const allSteps =
    question.steps.length > 0
      ? question.steps
      : [
          { kind: 'formula' as const, text: question.formula ?? 'Read it once more, out loud.' },
          { kind: 'result' as const, text: question.why },
        ];

  /*
   * Before she commits, the walk-through shows setup only — the formula, the
   * substitution, the convention that trips people up. The arithmetic steps
   * ('compute') and the answer ('result') wait until she has picked.
   *
   * Splitting by step kind rather than by position matters: several solvers
   * put the final number in their second-to-last compute line, so "everything
   * except the last step" would still hand over the answer. And setup is the
   * part she is actually stuck on — knowing which formula to reach for is the
   * whole difficulty, not the division at the end.
   */
  const SETUP_KINDS = new Set(['formula', 'substitute', 'note']);
  const setupOnly = allSteps.filter((s) => SETUP_KINDS.has(s.kind));
  const walk = session.revealed
    ? allSteps
    : setupOnly.length > 0
      ? setupOnly
      : allSteps.slice(0, 1);
  const holdingBack = walk.length < allSteps.length;

  const respond = (response: Response) => dispatch({ type: 'respond', response });

  return (
    <div style={{ padding: '12px 20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => dispatch({ type: 'exit' })}
          aria-label="Leave this set"
          style={{
            width: 44,
            height: 44,
            flex: 'none',
            borderRadius: 10,
            border: '1px solid var(--line-strong)',
            background: 'var(--surface-2)',
            color: 'var(--text-3)',
            font: '600 16px/1 var(--font-display)',
            cursor: 'pointer',
          }}
        >
          ←
        </button>
        <div style={{ flex: 1, display: 'flex', gap: 5 }} aria-hidden>
          {session.draws.map((draw, i) => (
            <div
              key={draw.seed}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 999,
                transition: 'background var(--motion)',
                background:
                  i < session.index
                    ? 'var(--lime)'
                    : i === session.index
                      ? 'rgba(255,255,255,.45)'
                      : 'rgba(255,255,255,.12)',
              }}
            />
          ))}
        </div>
        <div
          className="num"
          style={{ font: '600 12px/1 var(--font-body)', color: 'var(--text-4)' }}
        >
          {session.index + 1}/{total}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            font: '600 11px/1 var(--font-body)',
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--lime)',
          }}
        >
          {getTopic(session.topicId).name}
        </div>
        <h1
          style={{
            font: '600 21px/1.3 var(--font-display)',
            letterSpacing: '-.01em',
            textWrap: 'pretty',
          }}
        >
          {question.prompt}
        </h1>
        {question.sub && (
          <div
            style={{
              font: '400 14px/1.5 var(--font-body)',
              color: 'var(--text-3)',
              textWrap: 'pretty',
            }}
          >
            {question.sub}
          </div>
        )}
      </div>

      {question.kind === 'mc' && (
        <McList
          question={question}
          picked={typeof session.response === 'number' ? session.response : null}
          revealed={session.revealed}
          onPick={respond}
        />
      )}
      {question.kind === 'ruler' && (
        <Ruler
          picked={typeof session.response === 'number' ? session.response : null}
          answer={question.answerSixteenth}
          revealed={session.revealed}
          onPick={respond}
        />
      )}
      {question.kind === 'bolt' && (
        <BoltCircle
          holes={question.holes}
          picked={typeof session.response === 'number' ? session.response : null}
          answer={question.answerIndex}
          revealed={session.revealed}
          onPick={respond}
        />
      )}
      {question.kind === 'tri' && (
        <Triangle
          angleLabel={question.angleLabel}
          picked={typeof session.response === 'string' ? session.response : null}
          answer={question.answerSide}
          revealed={session.revealed}
          onPick={respond}
        />
      )}
      {question.kind === 'slider' && (
        <SliderInput
          question={question}
          value={typeof session.response === 'number' ? session.response : null}
          revealed={session.revealed}
          onChange={respond}
        />
      )}
      {question.kind === 'numeric' && (
        <NumericInput
          key={`${session.topicId}-${session.index}`}
          question={question}
          value={typeof session.response === 'number' ? session.response : null}
          revealed={session.revealed}
          onChange={respond}
        />
      )}

      {session.stepsShown > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 11,
            padding: '15px 16px',
            borderRadius: 14,
            background: 'var(--surface)',
            border: '1px solid var(--line-strong)',
          }}
        >
          <div
            style={{
              font: '600 11px/1 var(--font-body)',
              letterSpacing: '.13em',
              textTransform: 'uppercase',
              color: 'var(--text-4)',
            }}
          >
            One step at a time
          </div>
          {walk.slice(0, session.stepsShown).map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <div
                style={{
                  flex: 'none',
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  background: 'oklch(0.82 0.17 130 / .16)',
                  color: 'var(--lime-bright)',
                  font: '700 11px/20px var(--font-display)',
                  textAlign: 'center',
                }}
              >
                {i + 1}
              </div>
              <div
                className="num"
                style={{
                  font: '500 14px/1.45 var(--font-body)',
                  color: 'var(--text-2)',
                  textWrap: 'pretty',
                }}
              >
                {s.text}
              </div>
            </div>
          ))}
          {session.stepsShown < walk.length ? (
            <button
              type="button"
              onClick={() => dispatch({ type: 'nextStep' })}
              style={{ ...ghostButton, width: '100%', flex: 'none' }}
            >
              Next step
            </button>
          ) : (
            holdingBack && (
              <div style={{ font: '400 12.5px/1.45 var(--font-body)', color: 'var(--text-4)' }}>
                That is as far as it goes until you have had a go. Pick an answer — wrong is fine,
                you get the rest either way.
              </div>
            )
          )}
        </div>
      )}

      {session.revealed && (
        <div
          role="status"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
            padding: '15px 16px',
            borderRadius: 14,
            background: correct ? 'oklch(0.82 0.17 130 / .09)' : 'oklch(0.82 0.17 65 / .09)',
            border: `1px solid ${correct ? 'var(--lime-line)' : 'var(--amber-line)'}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div
              style={{
                width: 9,
                height: 9,
                borderRadius: 999,
                background: correct ? 'var(--lime)' : 'var(--amber)',
              }}
            />
            <div style={{ font: '700 16px/1.2 var(--font-display)' }}>
              {correct ? 'That is it.' : `Not quite — it is ${answerLabel(question)}.`}
            </div>
          </div>

          {/* The named mistake comes first: it is the most useful sentence
              on the screen, and only appears when she made that exact error. */}
          {trap && (
            <div
              style={{
                font: '500 14px/1.5 var(--font-body)',
                color: 'var(--text-2)',
                textWrap: 'pretty',
              }}
            >
              {trap}
            </div>
          )}

          <div
            style={{
              font: '400 14px/1.5 var(--font-body)',
              color: 'var(--text-3)',
              textWrap: 'pretty',
            }}
          >
            {question.why}
          </div>

          {question.formula && (
            <div
              className="num"
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                background: 'rgba(0,0,0,.28)',
                font: '700 14px/1.4 var(--font-display)',
                color: 'var(--lime-bright)',
              }}
            >
              {question.formula}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          type="button"
          onClick={() => dispatch({ type: 'check' })}
          disabled={!session.revealed && needsAnswer}
          style={{
            ...primaryButton,
            ...(!session.revealed && needsAnswer ? disabledButton : {}),
          }}
        >
          {session.revealed ? (last ? 'Finish' : 'Next') : 'Check'}
        </button>
        <div style={{ display: 'flex', gap: 9 }}>
          <button
            type="button"
            onClick={() => dispatch({ type: 'toggleWalk' })}
            style={ghostButton}
          >
            {session.stepsShown > 0 ? 'Hide the steps' : 'Walk me through it'}
          </button>
          <button type="button" onClick={() => dispatch({ type: 'park' })} style={ghostButton}>
            Park it
          </button>
        </div>
      </div>
    </div>
  );
}
